'use client';

import * as React from 'react';
import { Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useCookieConsentHydration } from '@/hooks/use-cookie-consent-hydration';
import { useCookieConsentStore } from '@/stores/cookie-consent-store';
import { VA_EVENTS } from '@/lib/analytics/event-catalog';
import { vaTrack } from '@/lib/analytics/vercel-events';
import Link from 'next/link';

const ACCEPT_COOKIE =
  'cookieConsent=true; expires=Fri, 31 Dec 9999 23:59:59 GMT; path=/; SameSite=Lax';

interface CookieConsentProps extends React.HTMLAttributes<HTMLDivElement> {
  onAcceptCallback?: () => void;
  onDeclineCallback?: () => void;
  description?: string;
  learnMoreHref?: string;
}

const CookieConsent = React.forwardRef<HTMLDivElement, CookieConsentProps>(
  (
    {
      onAcceptCallback = () => {},
      onDeclineCallback = () => {},
      className,
      description = 'We use cookies to ensure you get the best experience on our website. For more information on how we use cookies, please refer to the cookies section of our',
      ...props
    },
    ref,
  ) => {
    const persistAccept = useCookieConsentStore((s) => s.accept);
    const persistDecline = useCookieConsentStore((s) => s.decline);
    const persistedStatus = useCookieConsentStore((s) => s.status);

    const [isOpen, setIsOpen] = React.useState(false);
    const hydrated = useCookieConsentHydration();
    const [exiting, setExiting] = React.useState(false);

    React.useEffect(() => {
      if (!hydrated) return;
      if (persistedStatus === 'unset') setIsOpen(true);
    }, [hydrated, persistedStatus]);

    const handleAccept = React.useCallback(() => {
      setExiting(true);
      setIsOpen(false);
      vaTrack(VA_EVENTS.COOKIE_CONSENT_ACCEPT);
      try {
        document.cookie = ACCEPT_COOKIE;
      } catch (error) {
        console.warn('Cookie consent error:', error);
      }
      persistAccept();
      setTimeout(() => {
        setExiting(false);
      }, 700);
      onAcceptCallback();
    }, [persistAccept, onAcceptCallback]);

    const handleDecline = React.useCallback(() => {
      setExiting(true);
      setIsOpen(false);
      vaTrack(VA_EVENTS.COOKIE_CONSENT_DECLINE);
      persistDecline();
      setTimeout(() => {
        setExiting(false);
      }, 700);
      onDeclineCallback();
    }, [persistDecline, onDeclineCallback]);

    if (!hydrated) return null;
    if (persistedStatus !== 'unset' && !exiting) return null;

    return (
      <div
        ref={ref}
        className={cn(
          'fixed bottom-0 sm:bottom-4 left-0 sm:left-4 z-50 w-dvw sm:w-full sm:max-w-md transition-all duration-300 ease-in-out shadow-xs bg-neutral-50 dark:bg-neutral-900 max-sm:border-t sm:border rounded-t-xl sm:rounded-xl py-4 space-y-2 overflow-hidden',
          !isOpen ? 'translate-y-full opacity-0' : 'translate-y-0 opacity-100',
          className,
        )}
        {...props}
      >
        <div className="flex flex-row items-center justify-between px-4">
          <h3 className="text-lg font-medium text-foreground font-heading">
            This website uses cookies.
          </h3>
          <Cookie className="size-5 text-[#948B7F] dark:text-[#FCFAD7] shrink-0" />
        </div>
        <div className="space-y-2 px-4 pb-2">
          <p className="text-sm text-muted-foreground font-light text-pretty">
            {description}{' '}
            <Link
              href="/privacy-policy"
              className="text-foreground font-normal hover:text-[#BFB7A1] border-b hover:border-[#BFB7A1] transition-all duration-300 ease-in-out"
            >
              Privacy Policy
            </Link>
            . By clicking <span className="text-foreground">&quot;Accept all cookies&quot;</span>,
            you agree to our use of cookies.
          </p>
        </div>
        <div className="flex flex-row items-center justify-start gap-3 border-t pt-4 px-4">
          <Button
            onClick={handleDecline}
            variant="default"
            size="lg"
            className="bg-neutral-100 dark:bg-neutral-800 text-muted-foreground cursor-pointer rounded-lg border border-border hover:bg-[#BFB7A1] hover:text-white transition-all duration-300 ease-in-out w-fit px-6 h-9 min-w-32 font-semibold inline-flex items-center justify-center gap-2 group hover:border-[#BFB7A1]"
          >
            Decline
          </Button>
          <Button
            onClick={handleAccept}
            variant="default"
            size="lg"
            className="bg-[#62594F] text-white cursor-pointer rounded-lg hover:bg-[#6F665B] hover:text-white transition-all duration-300 ease-in-out w-full h-9 font-semibold inline-flex items-center justify-center gap-2 group flex-1"
          >
            Accept all cookies
          </Button>
        </div>
      </div>
    );
  },
);

CookieConsent.displayName = 'CookieConsent';
export { CookieConsent };
export default CookieConsent;
